import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import ChipsInput from 'components/v3/ChipsInput';
import { useFormRecipientSuggestions } from '../../gql';
import { LIMITS } from '../../shared/fieldTypes';
import { EMAIL_RE } from '../state/definitionValidation';
import { Hint } from './parts';

// "Send to" for new-submission emails (plan §6 A2): the account's team
// first, then admins (tagged "Admin", once 2+ characters are typed), and
// any valid email typed in full. Recipients are
// { email, name, user_id }; at most LIMITS.recipients.

const SEARCH_DELAY = 250;

const lower = (text) => String(text || '').toLowerCase();

export const recipientsHint = (count) => {
  if (!count)
    return 'No one is getting notified yet. Pick from the team or type an email address.';
  const who = count === 1 ? '1 person gets' : `${count} people get`;
  return `${who} an email with every new entry. Pick from the team or type any email address.`;
};

const noMatchText = (query) =>
  query.includes('@')
    ? 'Keep typing a full address, e.g. name@track.com'
    : `No one on the team matches “${query}” — enter an email address to add someone else.`;

const RecipientsPicker = ({ accountId, value, onChange, error }) => {
  const [search, { suggestions }] = useFormRecipientSuggestions();
  const [query, setQuery] = useState('');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const taken = new Set(value.map((r) => lower(r.email)));
  const full = value.length >= LIMITS.recipients;
  // The server filters too; this keeps a slower response from offering
  // someone who no longer matches what's typed.
  const matches = (s) =>
    !query ||
    [s.name, s.email, s.role].some((text) =>
      lower(text).includes(lower(query))
    );
  const people = full
    ? []
    : suggestions.filter((s) => !taken.has(lower(s.email)) && matches(s));

  const onQueryChange = (text) => {
    setQuery(text.trim());
    clearTimeout(timer.current);
    if (!accountId) return;
    timer.current = setTimeout(
      () => search(accountId, text.trim()),
      SEARCH_DELAY
    );
  };

  const validateFreeText = (text) =>
    !full &&
    EMAIL_RE.test(text) &&
    !taken.has(lower(text)) &&
    !people.some((p) => lower(p.email) === lower(text));

  const onAdd = (item) => {
    if (full) return;
    if (item.freeText) {
      onChange([...value, { email: item.label, name: null, user_id: null }]);
      return;
    }
    const person = people.find((p) => lower(p.email) === item.key);
    if (person) {
      onChange([
        ...value,
        {
          email: person.email,
          name: person.name || null,
          user_id: person.user_id,
        },
      ]);
    }
  };

  return (
    <>
      <ChipsInput
        label="Send to"
        placeholder="Type a name or email…"
        values={value.map((r) => ({
          key: lower(r.email),
          label: r.name || r.email,
          detail: r.email,
        }))}
        suggestions={people.map((p) => ({
          key: lower(p.email),
          label: p.name || p.email,
          detail: p.role ? `${p.email} · ${p.role}` : p.email,
          tag: p.on_team ? undefined : 'Admin',
        }))}
        onQueryChange={onQueryChange}
        onAdd={onAdd}
        onRemove={(key) =>
          onChange(value.filter((r) => lower(r.email) !== key))
        }
        validateFreeText={validateFreeText}
        freeTextHint="Not on the team — they’ll still get the emails"
        noMatchText={
          full
            ? `Send to ${LIMITS.recipients} people or fewer`
            : noMatchText(query)
        }
        error={!!error}
      />
      {error ? (
        <div
          role="alert"
          style={{ marginTop: 6, fontSize: 13, color: '#ED4337' }}
        >
          {error}
        </div>
      ) : null}
      <Hint top={8}>{recipientsHint(value.length)}</Hint>
    </>
  );
};

RecipientsPicker.propTypes = {
  accountId: PropTypes.number,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string.isRequired,
      name: PropTypes.string,
      user_id: PropTypes.number,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

RecipientsPicker.defaultProps = { accountId: null, error: null };

export default RecipientsPicker;
